import {
  ViewerConfig,
  RendererTypes,
  CoursewareStructure,
  ENVIRONMENTS,
  IAnnotation,
  setAppConfigOnWindowForTrackers,
  IAppTrackerConfig,
} from "@bronte/viewer/src/main";
import getAppConfig from "../src/config/appConfig";
import { generateCSS, logPayload } from "./shared/utils";
import {
  AnnotationsAPI,
  SpotlightAPI,
  SearchAPI,
  MediaAPI,
  SelectedThemePayload,
  SpotlightApplyOptions,
  ThemeOptions,
  ThemeVariants,
  TOCStitchedLessonMapItem,
} from "./shared/types";
import Globals from "@bronte/offline-services/src/utils/GlobalConstants";
import { getEncryptedFilesList } from "@bronte/offline-services/src/utils/DataUtils";
import { debounce } from "lodash";
import {
  BV_DISABLE_LOG_KEYWORDS,
  BV_ENABLE_LOG_KEYWORDS,
  SCREEN_PRINT_EVENT,
} from "./shared/constants";
import { IViewerApi } from "./IViewerApi";

declare global {
  interface Window {
    appTrackerConfig: IAppTrackerConfig;
  }
}

export class ViewerApi implements IViewerApi {
  #viewer: any;
  #appConfig!: ReturnType<typeof getAppConfig>;
  #selectedThemePayload!: SelectedThemePayload;
  #viewerConfig: ViewerConfig;
  private webStitchedLessonTocMap: Map<string, TOCStitchedLessonMapItem>;
  private prevSpotlightString = "";
  #isLogEnabled = false;
  #isBackGround = false;
  #backupRouteData: {
    elementId: string;
    subTarget: {
      annotation?: IAnnotation;
      dataId?: string;
      text?: string;
      screenId?: string;
    };
  } | null = null;

  constructor(viewerConfig: ViewerConfig) {
    this.#viewerConfig = viewerConfig;
    this.webStitchedLessonTocMap = new Map([]);
    this.#isLogEnabled = viewerConfig.enableLog || false;
    this.prevSpotlightString = "";

    if (!this.#validate()) {
      // todo: return error codes and messages
      console.error("Invalid initialization of Bronte Viewer.");
      return;
    }

    this.#init();
  }

  /**
   * todo: more validations
   * @returns
   */
  #validate = (): boolean => {
    if (this.#viewerConfig.renderer === RendererTypes.MOBILE && !this.#viewerConfig.packagePath) {
      // package path is required
      return false;
    }

    return true;
  };

  #init = async (): Promise<void> => {
    // TODO: Refactor the code to remove references of RendererTypes in code. (BRNT-21994)
    if (this.#isLiveWeb()) {
      this.#appConfig = getAppConfig(this.#viewerConfig.env ?? ENVIRONMENTS.STG);
      setAppConfigOnWindowForTrackers(this.#appConfig);
      const appConfig = this.#appConfig as unknown as {
        getGTMContainerId?: () => string;
        getGTMAuth?: () => string;
        getGTMPreview?: () => string;
      };
      const { getGTMContainerId, getGTMAuth, getGTMPreview } = appConfig;
      if (getGTMContainerId && getGTMAuth && getGTMPreview) {
        this.#viewerConfig.gtmConfig = {
          containerId: getGTMContainerId(),
          gtmAuth: getGTMAuth(),
          gtmPreview: getGTMPreview(),
        };
      }
      console.log("ViewerApi: Config loaded:", this.#appConfig);
    }

    // initialize the viewer after the config is loaded
    await import("@bronte/viewer/src/Viewer").then(async ({ Viewer }) => {
      this.#viewer = new Viewer(
        this.#viewerConfig,
        this.#appConfig?.getLearnerGraphqlEndpoint(),
        this.#appConfig?.getLearnerWebsocketURL(),
        this.#appConfig?.getCSGURL(),
        this.#appConfig?.getServiceHostName(),
        this.#appConfig?.getHarnessFMESDKKey(),
        this.#appConfig?.getHarnessFMEProjectId(),
        this.#appConfig?.getPILoginURL(),
        this.#appConfig?.getPIClientID(),
        this.#appConfig?.getLTConfig,
      );
    });
  };

  /**
   * If the Bronte Viewer is initialized w/o package path and renderer is WEB, then its a live WEB which connects to learnspace
   *
   * @returns true or false
   */
  #isLiveWeb = (): boolean =>
    !this.#viewerConfig.packagePath && this.#viewerConfig.renderer === RendererTypes.WEB;

  // dev hsell only method?
  //
  getCoursewareStructure = async (): Promise<CoursewareStructure> => {
    return this.#isLiveWeb()
      ? this.#viewer?.services.coursewareStructureService.getCoursewareStructure()
      : (window.ambrosia?.toc as CoursewareStructure);
  };

  // TODO: This method will be removed after implementation of BRNT-29613
  mapWebStitchedLessonToc = (
    stitchedLessonTocMap: Map<string, TOCStitchedLessonMapItem> | undefined,
  ): void => {
    if (stitchedLessonTocMap) {
      this.webStitchedLessonTocMap = stitchedLessonTocMap;
    }
  };

  getEncryptedFilesList = (elementId: string): string[] => getEncryptedFilesList(elementId);

  sendEvent = (key: string, value: any): void => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();
    eventService.publish(key, value);
  };

  print = (): void => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();
    eventService.publish(SCREEN_PRINT_EVENT);
  };

  // TODO : Need to remove screenId from subTarget after full web toc implementation
  #debouncedRoute = (
    elementId: string,
    subTarget: {
      annotation?: IAnnotation;
      dataId?: string;
      text?: string;
      screenId?: string;
    } = {},
  ): void => {
    if (this.#isBackGround) {
      this.#backupRouteData = {
        elementId,
        subTarget,
      };
      console.log("storing the route info as App is in background: ", this.#backupRouteData);
      return;
    }

    this.#backupRouteData = null;

    /**
     * @param elementToRoute can be chapter, lesson or screen.
     * @param screenId in case elementToRoute is the lesson id.
     */

    const navigateToRoute = (elementToRoute: string, screenId?: string): void => {
      /**
       * elementId can be the chapter id, lesson id, screenId.
       * subTarget may contains information like scroll to particular screen, annotation or page number while navigating to the given route.
       * Persisting this information to apply the scroll effect later once content is loaded on the screen.
       */
      this.#viewer.clientData.currentRoute = {
        elementId: elementToRoute,
        subTarget: {
          ...subTarget,
          ...(screenId && { screenId }), // saving screen id in case screen id is part of stitched lesson.
        },
        isLoaded: false,
      };

      this.#viewer.services.cdpServices
        .get("ROUTE_SERVICE")
        .build({
          target: {
            id: elementToRoute,
            subTarget: screenId && { screenId }, // Stitched lesson will use it to load this screen first before all other screens.
          },
        })
        .routeTo(subTarget);
    };

    if (this.#isLiveWeb()) {
      // TODO: The code here needs to change after the full web toc implementation. Need to leverage the code for offline BRNT-29613
      let screenId;
      // @ts-ignore:next-line
      const tocElement: TOCStitchedLessonMapItem | undefined =
        subTarget?.screenId && this.webStitchedLessonTocMap.get(subTarget.screenId);
      const stitchedElementToRoute = tocElement?.elementToRoute || elementId;
      if (stitchedElementToRoute !== this.#viewer.clientData?.currentRoute?.elementId) {
        // Reaching here means it'a new route, load it.
        if (tocElement?.isScreen && stitchedElementToRoute !== subTarget?.screenId) {
          // It means elementId is a screen inside stitched lesson.
          screenId = subTarget?.screenId;
        }
        return navigateToRoute(stitchedElementToRoute, screenId);
      }

      if (tocElement?.isScreen) {
        // Reaching here means it's a new screen in the same stitched lesson then scroll to it.

        this.#viewer.services.cdpServices.get("EVENT_SERVICE").build().publish("scrollTo", {
          screenId: subTarget.screenId,
          scrollIntoView: true,
        });

        return;
      }
    }

    if (subTarget?.annotation && elementId !== subTarget?.annotation?.screenId) {
      console.error("Annotation doesn't exist on the given route (elementId).");
    }

    const tocElement = Globals.tocMap.get(elementId);

    if (!tocElement) {
      throw new Error(`The element id ${elementId} doesn't exist.`);
    }

    const elementToRoute = tocElement.elementToRoute;

    if (elementToRoute !== this.#viewer.clientData?.currentRoute?.elementId) {
      // Reaching here means it'a new route, load it.
      let screenId = "";
      if (tocElement.isScreen && elementToRoute !== elementId) {
        // It means elementId is a screen inside stitched lesson.
        screenId = elementId;
      }
      return navigateToRoute(elementToRoute, screenId);
    }

    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

    if (subTarget?.annotation) {
      eventService.publish(
        `annotations.scroll.event.${subTarget.annotation.elementId}`,
        subTarget.annotation,
      );

      return;
    }

    if (subTarget?.dataId) {
      eventService.publish(`scroll.to.event`, {
        dataId: subTarget.dataId,
        text: subTarget.text,
      });
      return;
    }

    if (tocElement.isScreen) {
      // Reaching here means it's a new screen in the same stitched lesson then scroll to it.

      eventService.publish("scrollTo", {
        screenId: elementId,
        scrollIntoView: true,
      });

      return;
    }
  };

  route = debounce(this.#debouncedRoute, 500, {
    leading: true,
    trailing: false,
  });

  getEventService = () => this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

  getThemeVariants = async (): Promise<ThemeVariants> => {
    if (!this.#selectedThemePayload) {
      const themeService = this.#viewer.services.cdpServices.get("THEME_SERVICE").build({
        target: { id: this.#viewer.coursewareItemId },
      });

      this.#selectedThemePayload = await themeService.getThemeVariants();
    }

    return this.#selectedThemePayload?.themeVariants || {};
  };

  updateTheme = async (themeOptions: ThemeOptions): Promise<void> => {
    logPayload(this.#isLogEnabled, "Update theme option payload", themeOptions);

    if (!this.#selectedThemePayload) {
      await this.getThemeVariants();
    }

    if (this.#selectedThemePayload?.themeVariants) {
      const themeService = this.#viewer.services.cdpServices.get("THEME_SERVICE").build({
        target: { id: this.#viewer.coursewareItemId },
      });

      let selectedThemeVariant = await themeService.getThemeVariant(
        themeOptions.variantId,
        this.#selectedThemePayload.themeId || this.#selectedThemePayload.id, // id and themeId are the same but id is returned in offline and themeId is returned in web.
      );

      if (selectedThemeVariant) {
        const variantName = selectedThemeVariant.variantName;
        selectedThemeVariant = JSON.parse(selectedThemeVariant.config);
        selectedThemeVariant.css = selectedThemeVariant.css + generateCSS(themeOptions);
        selectedThemeVariant.variantName = variantName;
        selectedThemeVariant.continuousScroll = themeOptions.continuousScroll;
        await themeService.setInTheme(selectedThemeVariant);
      } else {
        console.error("Specified theme variant id not found.");
      }
    }
  };

  // media api to handle controls of mmi player (like play/pause) from bronte viewer api
  media = (): MediaAPI => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();
    return {
      play: (): void => {
        // TODO: Noop right now. media.play.event will be added later.
      },
      pause: (): void => {
        // this event is subscribed by bronte mmi component and will pause all the audio/video players on the screen if playing
        eventService.publish(`media.pause.event`);
      },
    };
  };

  annotations = (): AnnotationsAPI => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

    return {
      load: (annotations: IAnnotation[], options?: { hideAnnotations?: boolean }): void => {
        logPayload(this.#isLogEnabled, "Load annotation payload", annotations);

        /**
         * Update proxy watch object to trigger on annotation change subscription
         * changes to this object are listened by annotation change subscription in viewer.ts
         */
        this.#viewer.clientDataProxy.annotations = annotations;

        // We should check if hideAnnotations property is exist in options object and should not change persisted data if not exist, because it could trigger unwanted change of hide annotations setting.
        if (options?.hideAnnotations !== undefined) {
          this.#viewer.clientData.hideAnnotations = options?.hideAnnotations;
        }
      },
      add: (annotation: IAnnotation): void => {
        // This is the way to enable log from any client by adding a note
        if (BV_ENABLE_LOG_KEYWORDS.includes(annotation.noteText?.toLowerCase() ?? "")) {
          this.#isLogEnabled = true;
        }

        logPayload(this.#isLogEnabled, "Add annotation payload", annotation);

        // only disable log tracing if enableLog is false in viewer config at time of initialization
        if (
          BV_DISABLE_LOG_KEYWORDS.includes(annotation.noteText?.toLowerCase() ?? "") &&
          !this.#viewerConfig.enableLog
        ) {
          this.#isLogEnabled = false;
        }

        eventService.publish(`annotations.add.event.${annotation.elementId}`, annotation);
      },
      edit: (annotation: IAnnotation): void => {
        logPayload(this.#isLogEnabled, "Edit annotation payload", annotation);
        eventService.publish(`annotations.edit.event.${annotation.elementId}`, annotation);
      },
      delete: (annotation: IAnnotation): void => {
        logPayload(this.#isLogEnabled, "Delete annotation payload", annotation);

        // only disable log tracing if enableLog is false in viewer config at time of initialization
        if (
          BV_ENABLE_LOG_KEYWORDS.includes(annotation.noteText?.toLowerCase() ?? "") &&
          !this.#viewerConfig.enableLog
        ) {
          this.#isLogEnabled = false;
        }
        eventService.publish(`annotations.delete.event.${annotation.elementId}`, annotation);
      },
      show: (showAnnotations = true): void => {
        this.#viewer.clientData.hideAnnotations = !showAnnotations;
        eventService.publish(showAnnotations ? "annotations.show.event" : "annotations.hide.event");
      },
      clearSelection: (): void => {
        eventService.publish("annotations.clear.selection.event");
      },
    };
  };

  setIsAppBackgroundStatus = (isBackGround: boolean): void => {
    console.log("isAppBackgroundStatus: ", isBackGround);
    this.#isBackGround = isBackGround;
    if (!isBackGround && this.#backupRouteData) {
      console.log(
        "Routing to the latest Route triggered while in background: ",
        this.#backupRouteData,
      );
      this.#debouncedRoute(this.#backupRouteData.elementId, this.#backupRouteData.subTarget);
    }
  };

  #noopSpotlightApi: SpotlightAPI = {
    apply: (): void => {},
    clear: (): void => {},
  };

  spotlight = (): SpotlightAPI => {
    const cdpServices = this.#viewer?.services?.cdpServices;
    if (!cdpServices) {
      logPayload(
        this.#isLogEnabled,
        "Spotlight unavailable (viewer services not initialized)",
        false,
      );
      return this.#noopSpotlightApi;
    }

    const eventService = cdpServices.get("EVENT_SERVICE").build();
    return {
      apply: (options: SpotlightApplyOptions): void => {
        // this event is subscribed in Course plugin to update the time of the spotlight which is used to dynamically load screen
        eventService.publish("spotlight.time.update");
        if (this.#isBackGround) {
          console.log("Not performing the apply as the app is in background");
          return;
        }

        logPayload(this.#isLogEnabled, "Spotlight apply payload", options);

        // return if the current route is not loaded
        if (!this.#viewer.clientData?.currentRoute.isLoaded) {
          logPayload(
            this.#isLogEnabled,
            "Exiting early! 'Spotlight.apply' is called before route complete",
            false,
          );
          return;
        }

        const items = Array.isArray(options) ? options : [options];
        const validItems = items.filter((item) => {
          if (!item?.selector || /['"]/.test(item.selector)) {
            return false;
          }
          if (item.mark?.length) {
            return true;
          }
          return Boolean(item.useTextSearch && item.text?.trim());
        });

        if (!validItems.length) {
          logPayload(
            this.#isLogEnabled,
            "Exiting early! No valid spotlight entries (selector/mark or text search required)",
            options,
          );
          return;
        }

        // check if the apply event is getting called again with same options
        const currentSpotlightString = JSON.stringify(options);
        if (this.prevSpotlightString === currentSpotlightString) {
          logPayload(
            this.#isLogEnabled,
            "Exiting early! 'Spotlight.apply' is called again with the same options",
            false,
          );
          return;
        }
        this.prevSpotlightString = currentSpotlightString;

        const currentRouteInfo = {
          elementId: this.#viewer.clientData?.currentRoute?.elementId ?? null,
        };

        const isArrayApply = Array.isArray(options);
        validItems.forEach((item, index) => {
          eventService.publish("spotlight.apply.event", {
            ...item,
            currentRouteInfo,
            ...(isArrayApply
              ? index === 0
                ? { clearAllSpotlights: true }
                : {}
              : { replaceActiveSpotlights: true }),
          });
        });
      },
      clear: (): void => {
        if (this.#isBackGround) {
          console.log("Not performing the clear as the app is in background");
          return;
        }

        logPayload(this.#isLogEnabled, "Spotlight clear", false);
        this.prevSpotlightString = "";
        eventService.publish("spotlight.clear.event");
      },
    };
  };

  restart = (parentActivityId: string | undefined): void => {
    logPayload(this.#isLogEnabled, "Restart parentActivityId payload", parentActivityId ?? "");

    if (!parentActivityId) {
      console.error("Unable to reset the course.");
      return;
    }

    this.#viewer.services.cdpServices
      .get("LEARNER_SERVICE")
      .build({
        id: parentActivityId,
      })
      .restart(parentActivityId);
  };

  search = (): SearchAPI => {
    return {
      clear: (): void => {
        this.spotlight().clear();
      },
    };
  };

  onNetworkStatusChange = (isOnline: boolean): void => {
    logPayload(this.#isLogEnabled, "onNetworkStatusChange payload", isOnline);
    this.#viewer.services.cdpServices.get("CLASS_SERVICE").build().onNetworkStatusChange(isOnline);
  };

  enableLog = (): void => {
    this.#isLogEnabled = true;
  };

  disableLog = (): void => {
    this.#isLogEnabled = false;
  };
}
